import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { BreadcrumbItem } from '@/components/layout/Breadcrumbs';
import { resolveModuleFromPath } from '@/config/appNavigation';
import { getModuleEntryByErpModule } from '@/config/moduleEntries';
import { moduleSharedPath, type SharedMasterPage } from '@/config/moduleSharedPaths';

export function buildModuleScopedBreadcrumbs(
  pathname: string,
  pageLabel: string,
  parent?: { label: string; page: SharedMasterPage },
): BreadcrumbItem[] {
  const erpModule = resolveModuleFromPath(pathname);
  const entry = erpModule ? getModuleEntryByErpModule(erpModule) : undefined;
  const crumbs: BreadcrumbItem[] = [
    {
      label: entry?.label ?? 'Point of Sale',
      to: entry?.homePath ?? '/pos/dashboard',
    },
  ];
  if (parent && erpModule) {
    crumbs.push({ label: parent.label, to: moduleSharedPath(erpModule, parent.page) });
  }
  crumbs.push({ label: pageLabel });
  return crumbs;
}

export function resolveModuleSharedPath(pathname: string, page: SharedMasterPage): string {
  const erpModule = resolveModuleFromPath(pathname);
  if (erpModule) return moduleSharedPath(erpModule, page);
  return moduleSharedPath('POS', page);
}

export function useModuleScopedBreadcrumbs(
  pageLabel: string,
  parent?: { label: string; page: SharedMasterPage },
): BreadcrumbItem[] {
  const { pathname } = useLocation();
  return useMemo(
    () => buildModuleScopedBreadcrumbs(pathname, pageLabel, parent),
    [pathname, pageLabel, parent?.label, parent?.page],
  );
}

export function useModuleSharedPath(page: SharedMasterPage): string {
  const { pathname } = useLocation();
  return useMemo(() => resolveModuleSharedPath(pathname, page), [pathname, page]);
}
